import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { supabase } from '../lib/supabase';
import { formatMileage } from '../utils/dateUtils';
import { cropStyle } from '../utils/photoCrop';
import { calcCollectorScore } from './ClassicCarIntelligence';

// ── Vehicle Passport ─────────────────────────────────────────────────────────
// A sale-ready summary of one car. By default it shows only what a buyer needs:
// identity, specification, condition, originality, provenance, restoration,
// service and inspection history, recorded mileage and the paperwork on file.
// The owner's money (purchase price, running costs, insurance, investment
// analysis) is hidden unless switched on in the toolbar, and the estimated value
// is a separate switch, so the page is safe to hand to a buyer as it opens.
// Nothing prints automatically — the Print / Save PDF button is in the toolbar.

const money = (n) =>
  n !== null && n !== undefined && n !== '' && !isNaN(n)
    ? `€${Number(n).toLocaleString('en-IE', { maximumFractionDigits: 0 })}`
    : '—';
const longDate = (d) => {
  if (!d) return '—';
  try { return format(parseISO(String(d)), 'd MMMM yyyy'); } catch { return '—'; }
};
const shortDate = (d) => {
  if (!d) return '—';
  try { return format(parseISO(String(d)), 'dd MMM yyyy'); } catch { return '—'; }
};
const isFuture = (d) => { try { return parseISO(String(d)) >= new Date(new Date().setHours(0, 0, 0, 0)); } catch { return false; } };
const sum = (rows, key) => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);

export const VehiclePassport = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showValue, setShowValue] = useState(false);     // estimated value + market range
  const [showPrivate, setShowPrivate] = useState(false); // owner's financials

  useEffect(() => {
    const load = async () => {
      const [v, val, ph, ins, svc, mech, rt, log, docs, td, exp, rep] = await Promise.all([
        supabase.from('vehicles').select('*').eq('id', id).single(),
        supabase.from('vehicle_valuations').select('*').eq('vehicle_id', id).maybeSingle(),
        supabase.from('vehicle_photos').select('*').eq('vehicle_id', id).order('created_at', { ascending: true }),
        supabase.from('insurance_records').select('*').eq('vehicle_id', id).order('expiry_date', { ascending: false }),
        supabase.from('service_records').select('*').eq('vehicle_id', id).order('service_date', { ascending: false }),
        supabase.from('mechanical_reports').select('*').eq('vehicle_id', id).order('report_date', { ascending: false }),
        supabase.from('road_tax_records').select('*').eq('vehicle_id', id).order('expiry_date', { ascending: false }),
        supabase.from('vehicle_logbook').select('entry_date, mileage').eq('vehicle_id', id).not('mileage', 'is', null).order('entry_date', { ascending: true }).limit(500),
        supabase.from('documents').select('doc_type').eq('vehicle_id', id),
        supabase.from('title_deeds').select('id, issue_date').eq('vehicle_id', id),
        supabase.from('expenses').select('id, expense_date, category, description, supplier, amount').eq('vehicle_id', id).order('expense_date', { ascending: false }),
        supabase.from('market_intelligence_reports').select('*').eq('vehicle_id', id).order('generated_at', { ascending: false }).limit(1),
      ]);
      setData({
        vehicle: v.data, valuation: val.data || null, photos: ph.data || [],
        insurance: ins.data || [], services: svc.data || [], mechanical: mech.data || [],
        roadTax: rt.data || [], logbook: log.data || [], documents: docs.data || [],
        titleDeeds: td.data || [], expenses: exp.data || [], report: rep.data?.[0] || null,
      });
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Georgia, serif', fontSize: 18, color: '#1a1a2e' }}>
      Preparing Vehicle Passport…
    </div>
  );
  if (!data?.vehicle) return <div style={{ padding: 40, fontFamily: 'Georgia, serif' }}>Vehicle not found.</div>;

  const { vehicle, valuation, photos, insurance, services, mechanical, roadTax, logbook, documents, titleDeeds, expenses, report } = data;

  // ── Derived data ───────────────────────────────────────────────────────────
  const score = valuation ? calcCollectorScore(valuation) : null;   // same function as Car Intelligence
  const vin = vehicle.vin || valuation?.vin || '';
  const hero = photos.find(p => p.is_primary) || photos[0] || null;
  const galleryPhotos = photos.filter(p => p !== hero).slice(0, 12);
  const photoUrl = (p) => p?.url || p?.photo_url;

  const latestMech = mechanical[0] || null;
  const latestRt = roadTax[0] || null;
  const inspectionValid = !!(latestMech?.next_due_date && isFuture(latestMech.next_due_date) && latestMech.result === 'Passed');
  const restorationWork = expenses.filter(e => e.category === 'Restoration');

  // Recorded odometer readings: service visits + logbook mileage entries.
  const readings = [
    ...services.filter(s => s.mileage_at_service && s.service_date)
      .map(s => ({ date: String(s.service_date).slice(0, 10), km: Number(s.mileage_at_service), source: 'Service' })),
    ...logbook.filter(l => l.mileage && l.entry_date)
      .map(l => ({ date: String(l.entry_date).slice(0, 10), km: Number(l.mileage), source: 'Logbook' })),
  ].sort((a, b) => a.date.localeCompare(b.date) || a.km - b.km);
  let runningMax = 0; let mileageConsistent = true;
  readings.forEach(r => { if (r.km < runningMax) mileageConsistent = false; runningMax = Math.max(runningMax, r.km); });
  const latestReadings = [...readings].reverse().slice(0, 8);

  // Paperwork on file (counts only — the documents themselves are not printed).
  const docCount = (type) => documents.filter(d => d.doc_type === type).length;
  const paperwork = [
    ['Title deed', titleDeeds.length],
    ['Classic ID certificate', vehicle.classic_id_doc_url ? 1 : 0],
    ['Registration document', docCount('Registration')],
    ['MOT / inspection certificates', mechanical.filter(m => m.document_url).length + docCount('MOT/KTEO')],
    ['Service invoices', services.filter(s => s.invoice_url).length + docCount('Invoice')],
    ['Restoration receipts', restorationWork.filter(r => r.receipt_url).length],
    ['Original manuals', valuation?.original_manuals ? 1 : 0],
  ];

  // Selling points — only things the owner has actually recorded.
  const points = [];
  if (valuation) {
    if (valuation.matching_numbers) points.push('Matching numbers');
    if (valuation.original_paint) points.push('Original paint');
    if (valuation.original_interior) points.push('Original interior');
    if (valuation.single_owner) points.push('Single owner');
    if (valuation.service_history_status === 'Complete') points.push('Complete service history');
    else if (valuation.service_history_status === 'Partial') points.push('Partial service history');
    if (valuation.specialist_servicing) points.push('Specialist serviced');
    if (valuation.original_manuals) points.push('Original manuals');
    if (valuation.matching_vin_docs) points.push('Matching VIN documents');
    if (valuation.restoration_status === 'Original Survivor') points.push('Unrestored original');
    else if (['Restored', 'Partially Restored'].includes(valuation.restoration_status)) {
      points.push([
        valuation.restoration_status === 'Restored' ? 'Restored' : 'Partly restored',
        valuation.restoration_year ? `(${valuation.restoration_year})` : '',
        valuation.frame_off_restoration ? '· frame-off' : valuation.professional_restoration ? '· professional' : '',
      ].filter(Boolean).join(' '));
    }
    if (valuation.special_edition) points.push(valuation.total_production ? `Limited edition (${Number(valuation.total_production).toLocaleString()} built)` : 'Special / limited edition');
    else if (valuation.total_production && Number(valuation.total_production) < 5000) points.push(`Only ${Number(valuation.total_production).toLocaleString()} built`);
    if (valuation.museum_history) points.push('Museum history');
    if (valuation.celebrity_owned) points.push('Celebrity owned');
    if (valuation.factory_wheels) points.push('Factory wheels');
  }
  if (vehicle.classic_id) points.push('Classic ID certificate');
  if (inspectionValid) points.push(`MOT / inspection valid to ${shortDate(latestMech.next_due_date)}`);
  if (latestRt?.expiry_date && isFuture(latestRt.expiry_date)) points.push(`Road tax paid to ${shortDate(latestRt.expiry_date)}`);

  // Owner-only money figures (same definition of "invested" as Car Intelligence:
  // purchase price + every cost recorded against the car).
  const purchasePrice = valuation?.purchase_price ? Number(valuation.purchase_price) : null;
  const otherSpend = sum(expenses, 'amount') + sum(insurance, 'cost') + sum(roadTax, 'amount_paid') + sum(services, 'cost') + sum(mechanical, 'cost');
  const totalInvested = purchasePrice !== null ? purchasePrice + otherSpend : null;
  const currentValue = valuation?.current_value ? Number(valuation.current_value) : null;
  const gain = totalInvested !== null && currentValue !== null ? currentValue - totalInvested : null;
  const gainPct = gain !== null && totalInvested ? (gain / totalInvested) * 100 : null;
  const verdictColor = { 'Strong Hold': '#166534', 'Hold': '#1e40af', 'Consider Selling': '#92400e', 'Sell': '#991b1b' }[report?.investment_verdict] || '#1e40af';

  // Pre-sale completeness (screen only — never printed).
  const checks = [
    ['VIN / chassis number', !!vin],
    ['Current mileage', !!vehicle.mileage],
    ['At least 6 photos', photos.length >= 6],
    ['Condition grade (Car Intelligence)', !!valuation?.condition_grade],
    ['Service records', services.length > 0],
    ['Valid MOT / inspection on file', inspectionValid],
    ['Road tax current', !!(latestRt?.expiry_date && isFuture(latestRt.expiry_date))],
    ['Title deed on file', titleDeeds.length > 0],
    ['Odometer readings consistent', mileageConsistent],
    ['Factory colour and interior', !!(valuation?.original_colour_name || valuation?.interior_colour)],
  ];
  const missing = checks.filter(([, ok]) => !ok).map(([label]) => label);

  const specRows = [
    ['Make', vehicle.brand],
    ['Model', vehicle.model],
    ['Variant', vehicle.variant],
    ['Generation', vehicle.generation],
    ['Body style', vehicle.body_style],
    ['Year', vehicle.year],
    ['Engine', vehicle.engine_size],
    ['Transmission', valuation?.transmission_type],
    ['Fuel', vehicle.fuel_type],
    ['Mileage', vehicle.mileage ? formatMileage(vehicle.mileage) : null],
    ['Colour', vehicle.color],
    ['Factory colour', valuation?.original_colour_name],
    ['Interior', [valuation?.interior_colour, valuation?.interior_material].filter(Boolean).join(' · ')],
    ['Registration', vehicle.registration_number, 'mono'],
    ['VIN / chassis', vin, 'mono'],
    ['Engine number', vehicle.engine_number, 'mono'],
    ['Classic ID', vehicle.classic_id, 'mono'],
    ['Build date', valuation?.build_date ? longDate(valuation.build_date) : null],
    ['Factory order no.', valuation?.factory_order_number, 'mono'],
    ['Units produced', valuation?.total_production ? Number(valuation.total_production).toLocaleString() : null],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  const checklist = valuation ? [
    ['Original paint', valuation.original_paint], ['Matching numbers', valuation.matching_numbers],
    ['Original interior', valuation.original_interior], ['OEM parts', valuation.oem_parts],
    ['Factory wheels', valuation.factory_wheels], ['Original radio', valuation.original_radio],
    ['No modifications', valuation.no_modifications], ['Single owner', valuation.single_owner],
    ['Matching VIN documents', valuation.matching_vin_docs], ['Specialist serviced', valuation.specialist_servicing],
    ['Original manuals', valuation.original_manuals], ['Restoration documented', valuation.major_restoration_docs],
    ['Museum history', valuation.museum_history], ['Special / limited edition', valuation.special_edition],
  ] : [];

  const closePassport = () => {
    if (window.opener) window.close(); else window.location.href = `/vehicles/${id}`;
  };

  return (
    <>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Georgia, 'Times New Roman', serif; background: #e5e7eb; color: #1a1a2e; }
        .toolbar { position: sticky; top: 0; z-index: 50; background: #0d1b3e; color: white; font-family: Arial, sans-serif; }
        .toolbar-inner { max-width: 210mm; margin: 0 auto; padding: 12px 16px; display: flex; gap: 14px; align-items: center; justify-content: space-between; flex-wrap: wrap; }
        .toolbar label { display: flex; align-items: flex-start; gap: 8px; font-size: 12px; line-height: 1.35; cursor: pointer; max-width: 300px; color: rgba(255,255,255,0.85); }
        .toolbar label input { margin-top: 2px; }
        .toolbar-btn { border: none; padding: 9px 18px; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 600; font-family: Arial, sans-serif; }
        .btn-print { background: #c9a84c; color: #0d1b3e; }
        .btn-close { background: rgba(255,255,255,0.14); color: white; }
        .check-panel { max-width: 210mm; margin: 12px auto 0; padding: 0 16px; font-family: Arial, sans-serif; }
        .check-panel-box { background: white; border: 1px solid #d1d5db; border-left: 4px solid #c9a84c; border-radius: 8px; padding: 10px 14px; font-size: 12.5px; color: #374151; line-height: 1.5; }
        .check-panel-box.done { border-left-color: #16a34a; }

        .passport { max-width: 210mm; margin: 14px auto 40px; background: white; box-shadow: 0 6px 30px rgba(0,0,0,0.18); }
        .cover { background: linear-gradient(135deg, #0d1b3e 0%, #1a2a5e 100%); color: white; padding: 36px 40px 28px; min-height: 297mm; display: flex; flex-direction: column; justify-content: space-between; gap: 22px; }
        .cover-top { display: flex; justify-content: space-between; align-items: flex-start; }
        .fp-logo { display: flex; align-items: center; gap: 10px; }
        .fp-logo-title { font-size: 14px; font-weight: 700; letter-spacing: 3px; color: white; text-transform: uppercase; font-family: Arial, sans-serif; }
        .fp-logo-sub { font-size: 8px; color: #c9a84c; letter-spacing: 4px; text-transform: uppercase; margin-top: 2px; font-family: 'Courier New', monospace; }
        .passport-label { font-size: 9px; letter-spacing: 3px; text-transform: uppercase; color: #c9a84c; border: 1px solid #c9a84c; padding: 4px 10px; border-radius: 3px; font-family: Arial, sans-serif; }
        .cover-hero { flex: none; border-radius: 10px; overflow: hidden; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); }
        .cover-hero img { width: 100%; height: auto; max-height: 125mm; object-fit: cover; display: block; }
        .cover-hero-empty { min-height: 80mm; }
        .cover-hero-empty { height: 100%; display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,0.3); font-family: Arial, sans-serif; font-size: 12px; letter-spacing: 2px; text-transform: uppercase; }
        .reg-number { font-family: 'Courier New', monospace; font-size: 14px; font-weight: 700; color: #c9a84c; letter-spacing: 3px; text-transform: uppercase; margin-bottom: 8px; }
        .vehicle-name { font-size: 36px; font-weight: 700; letter-spacing: -0.5px; line-height: 1.1; margin-bottom: 6px; }
        .vehicle-sub { font-size: 15px; color: rgba(255,255,255,0.6); font-family: Arial, sans-serif; }
        .cover-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; border-top: 1px solid rgba(255,255,255,0.15); padding-top: 20px; }
        .cover-stat-label { font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,0.45); margin-bottom: 4px; font-family: Arial, sans-serif; }
        .cover-stat-value { font-size: 17px; font-weight: 700; color: white; }
        .cover-stat-value.gold { color: #c9a84c; }
        .generated { font-size: 9px; color: rgba(255,255,255,0.35); font-family: Arial, sans-serif; }

        .body { padding: 12mm 15mm 8mm; }
        .section { padding: 22px 0; border-bottom: 1px solid #e5e7eb; }
        .section:last-of-type { border-bottom: none; }
        .section-title { font-size: 11px; letter-spacing: 3px; text-transform: uppercase; color: #6b7280; margin-bottom: 14px; font-family: Arial, sans-serif; display: flex; align-items: center; gap: 8px; }
        .section-title::after { content: ''; flex: 1; height: 1px; background: #e5e7eb; }
        .spec-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 14px 20px; }
        .field-label { font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: #9ca3af; margin-bottom: 3px; font-family: Arial, sans-serif; }
        .field-value { font-size: 14px; color: #1a1a2e; font-weight: 500; overflow-wrap: anywhere; }
        .field-value.mono { font-family: 'Courier New', monospace; font-size: 12.5px; color: #1e40af; }
        .chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .chip { font-family: Arial, sans-serif; font-size: 12px; color: #0d1b3e; background: #f3efe0; border: 1px solid #e3d9ad; border-radius: 999px; padding: 5px 12px; }
        .note { font-size: 11px; color: #6b7280; font-family: Arial, sans-serif; line-height: 1.5; margin-top: 10px; }

        .score-row { display: flex; gap: 24px; align-items: flex-start; flex-wrap: wrap; }
        .score-circle { width: 84px; height: 84px; border-radius: 50%; border: 3px solid #0d1b3e; display: flex; align-items: center; justify-content: center; flex-direction: column; flex-shrink: 0; }
        .score-number { font-size: 28px; font-weight: 700; color: #0d1b3e; line-height: 1; }
        .score-label { font-size: 8px; color: #6b7280; font-family: Arial, sans-serif; }
        .score-bars { flex: 1; min-width: 220px; }
        .score-bar-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
        .score-bar-label { font-size: 10px; color: #6b7280; width: 96px; font-family: Arial, sans-serif; }
        .score-bar-track { flex: 1; height: 6px; background: #f3f4f6; border-radius: 3px; overflow: hidden; }
        .score-bar-fill { height: 100%; background: #0d1b3e; border-radius: 3px; }
        .score-bar-val { font-size: 10px; color: #1a1a2e; font-weight: 600; width: 58px; text-align: right; font-family: Arial, sans-serif; }
        .score-bar-val.muted { color: #9ca3af; font-weight: 400; }

        .check-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 8px; margin-top: 18px; }
        .check-item { display: flex; align-items: center; gap: 6px; font-size: 11px; font-family: Arial, sans-serif; color: #374151; }
        .check-item.no { color: #9ca3af; }
        .check-box { width: 14px; height: 14px; border-radius: 3px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 9px; color: white; }
        .check-yes { background: #16a34a; }
        .check-no { background: #e5e7eb; }

        .record-table { width: 100%; border-collapse: collapse; font-family: Arial, sans-serif; }
        .record-table th { font-size: 9px; letter-spacing: 1px; text-transform: uppercase; color: #9ca3af; padding: 6px 8px; text-align: left; border-bottom: 1px solid #e5e7eb; }
        .record-table td { font-size: 11px; padding: 8px; border-bottom: 1px solid #f3f4f6; color: #374151; vertical-align: top; }
        .record-table tr { break-inside: avoid; }
        .record-table tr:last-child td { border-bottom: none; }
        .table-scroll { overflow-x: auto; }
        .verdict-badge { display: inline-block; padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: 700; font-family: Arial, sans-serif; }
        .money-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 14px 20px; }
        .forecast { background: #f9fafb; border-radius: 6px; padding: 10px 12px; border: 1px solid #e5e7eb; }

        .paper-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 8px 20px; }
        .paper-item { display: flex; justify-content: space-between; gap: 10px; font-size: 12px; font-family: Arial, sans-serif; padding: 6px 0; border-bottom: 1px dotted #d1d5db; color: #374151; }
        .paper-item.none { color: #9ca3af; }

        .photo-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
        .photo-item { aspect-ratio: 4/3; position: relative; border-radius: 6px; overflow: hidden; background: #f3f4f6; break-inside: avoid; }
        .photo-item img { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

        .passport-footer { border-top: 2px solid #0d1b3e; padding: 14px 0 4px; display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; margin-top: 24px; break-inside: avoid; }
        .footer-brand { font-family: Arial, sans-serif; font-size: 10px; color: #6b7280; line-height: 1.5; }
        .footer-brand strong { color: #0d1b3e; }
        .footer-disclaimer { font-size: 8.5px; color: #9ca3af; font-family: Arial, sans-serif; max-width: 360px; text-align: right; line-height: 1.5; }
        .section { break-inside: auto; }
        .section-title, .field-label { break-after: avoid; }
        .keep { break-inside: avoid; }

        @media (max-width: 720px) {
          .cover { padding: 24px 20px 20px; min-height: 0; }
          .cover-hero img { max-height: 70vh; }
          .cover-stats { grid-template-columns: repeat(2, 1fr); }
          .vehicle-name { font-size: 28px; }
          .body { padding: 8px 16px 8px; }
          .photo-grid { grid-template-columns: repeat(2, 1fr); }
          .passport-footer { flex-direction: column; }
          .footer-disclaimer { text-align: left; max-width: none; }
        }

        @page { size: A4; margin: 14mm 0; }
        @page cover { margin: 0; }
        @media print {
          body { background: white; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .no-print { display: none !important; }
          .passport { box-shadow: none; margin: 0; max-width: none; }
          .cover { page: cover; height: 297mm; min-height: 297mm; break-after: page; }
          .body { padding: 0 15mm; }
        }
      `}</style>

      {/* ── Toolbar and pre-sale check (screen only) ── */}
      <div className="toolbar no-print">
        <div className="toolbar-inner">
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
            <label>
              <input type="checkbox" checked={showValue} onChange={e => setShowValue(e.target.checked)} />
              <span>Show estimated value and market range</span>
            </label>
            <label>
              <input type="checkbox" checked={showPrivate} onChange={e => setShowPrivate(e.target.checked)} />
              <span>Show my financials (purchase price, costs, insurance, investment analysis). Leave off when sharing with a buyer.</span>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="toolbar-btn btn-print" onClick={() => window.print()}>Print / Save PDF</button>
            <button className="toolbar-btn btn-close" onClick={closePassport}>Close</button>
          </div>
        </div>
      </div>
      <div className="check-panel no-print">
        <div className={`check-panel-box${missing.length === 0 ? ' done' : ''}`}>
          <strong>Passport completeness: {checks.length - missing.length} of {checks.length}.</strong>{' '}
          {missing.length === 0
            ? 'Everything a buyer usually asks for is on this passport.'
            : <>Not yet on this passport: {missing.join(', ')}. Add these to the vehicle and refresh to include them.</>}
          {!mileageConsistent && <> Some odometer readings are lower than earlier ones, so check the mileage figures.</>}
        </div>
      </div>

      <div className="passport">

        {/* ── COVER ── */}
        <div className="cover">
          <div className="cover-top">
            <div className="fp-logo">
              <svg width="32" height="38" viewBox="0 0 38 44" fill="none">
                <path d="M19 2 L35 9 L35 24 Q35 36 19 42 Q3 36 3 24 L3 9 Z" fill="transparent" stroke="#c9a84c" strokeWidth="1.8" strokeLinejoin="round" />
                <text x="19" y="27" textAnchor="middle" fontSize="13" fontWeight="700" fontFamily="Arial, sans-serif" fill="#c9a84c" letterSpacing="-0.5">CC</text>
              </svg>
              <div>
                <div className="fp-logo-title">Car Collector</div>
                <div className="fp-logo-sub">Studio</div>
              </div>
            </div>
            <div className="passport-label">Vehicle Passport</div>
          </div>

          <div className="cover-hero">
            {hero
              ? <img src={photoUrl(hero)} alt={`${vehicle.brand} ${vehicle.model}`} style={{ objectPosition: cropStyle(hero).objectPosition }} />
              : <div className="cover-hero-empty">No photo added yet</div>}
          </div>

          <div>
            <div className="reg-number">{vehicle.registration_number}</div>
            <div className="vehicle-name">{vehicle.brand} {vehicle.model}</div>
            <div className="vehicle-sub">
              {[vehicle.year, vehicle.variant, vehicle.generation, vehicle.body_style].filter(Boolean).join(' · ')}
            </div>
          </div>

          <div className="cover-stats">
            {[
              { label: 'Condition', value: valuation?.condition_grade || '—', cls: '' },
              { label: 'Mileage', value: vehicle.mileage ? formatMileage(vehicle.mileage, { both: false }) : '—', cls: '' },
              { label: 'Collector Score', value: score ? `${score.total}/100` : '—', cls: 'gold' },
              showValue
                ? { label: 'Estimated Value', value: money(valuation?.current_value), cls: 'gold' }
                : { label: 'Service History', value: valuation?.service_history_status || (services.length ? `${services.length} records` : '—'), cls: '' },
            ].map((s, i) => (
              <div key={i}>
                <div className="cover-stat-label">{s.label}</div>
                <div className={`cover-stat-value ${s.cls}`}>{s.value}</div>
              </div>
            ))}
          </div>

          <div className="generated">
            Generated {format(new Date(), 'd MMMM yyyy')} · Car Collector Studio · carcollectorstudio.com
          </div>
        </div>

        {/* ── BODY ── */}
        <div className="body">

          {points.length > 0 && (
            <div className="section keep">
              <div className="section-title">Highlights</div>
              <div className="chips">{points.slice(0, 14).map(p => <span key={p} className="chip">{p}</span>)}</div>
            </div>
          )}

          <div className="section">
            <div className="section-title">Specification &amp; Identity</div>
            <div className="spec-grid">
              {specRows.map(([k, v, cls]) => (
                <div key={k}>
                  <div className="field-label">{k}</div>
                  <div className={`field-value ${cls || ''}`}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          {valuation && score && (
            <div className="section keep">
              <div className="section-title">Condition, Originality &amp; Provenance</div>
              <div className="score-row">
                <div className="score-circle">
                  <div className="score-number">{score.total}</div>
                  <div className="score-label">Collector Score</div>
                </div>
                <div className="score-bars">
                  {Object.entries(score.breakdown).map(([name, b]) => (
                    <div key={name} className="score-bar-row">
                      <div className="score-bar-label">{name}</div>
                      <div className="score-bar-track">
                        <div className="score-bar-fill" style={{ width: b.recorded ? `${(b.score / b.max) * 100}%` : '0%' }} />
                      </div>
                      <div className={`score-bar-val${b.recorded ? '' : ' muted'}`}>{b.recorded ? `${b.score}/${b.max}` : 'not recorded'}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="note">
                Collector Score is calculated from the details recorded by the owner ({score.completeness}% of categories recorded).
                Categories not recorded are marked and do not count as faults.
              </div>
              <div className="check-grid">
                {checklist.map(([label, on]) => (
                  <div key={label} className={`check-item${on ? '' : ' no'}`}>
                    <div className={`check-box ${on ? 'check-yes' : 'check-no'}`}>{on ? '✓' : ''}</div>
                    {label}
                  </div>
                ))}
              </div>
              {valuation.provenance_notes && (
                <div style={{ marginTop: 16 }}>
                  <div className="field-label">Provenance notes</div>
                  <div style={{ fontSize: 13, color: '#374151', fontFamily: 'Arial, sans-serif', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{valuation.provenance_notes}</div>
                </div>
              )}
            </div>
          )}

          {(valuation?.restoration_status || restorationWork.length > 0 || valuation?.modifications_list) && (
            <div className="section">
              <div className="section-title">Restoration &amp; Modifications</div>
              {valuation?.restoration_status && (
                <div className="spec-grid" style={{ marginBottom: 14 }}>
                  {[
                    ['Status', valuation.restoration_status],
                    ['Restoration year', valuation.restoration_year],
                    ['Professional', valuation.professional_restoration ? 'Yes' : null],
                    ['Frame-off', valuation.frame_off_restoration ? 'Yes' : null],
                    ['Documented', valuation.major_restoration_docs ? 'Yes' : null],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}><div className="field-label">{k}</div><div className="field-value">{v}</div></div>
                  ))}
                </div>
              )}
              {restorationWork.length > 0 && (
                <div className="table-scroll keep">
                  <table className="record-table">
                    <thead><tr><th>Date</th><th>Work</th><th>Carried out by</th>{showPrivate && <th>Cost</th>}</tr></thead>
                    <tbody>
                      {restorationWork.map(r => (
                        <tr key={r.id}>
                          <td>{shortDate(r.expense_date)}</td>
                          <td>{r.description || 'Restoration work'}</td>
                          <td>{r.supplier || '—'}</td>
                          {showPrivate && <td>{money(r.amount)}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {valuation?.modifications_list && (
                <div style={{ marginTop: 16 }}>
                  <div className="field-label">Modifications{showValue && valuation.modification_impact ? ` · impact on value: ${valuation.modification_impact}` : ''}</div>
                  <div style={{ fontSize: 13, color: '#374151', fontFamily: 'Arial, sans-serif', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{valuation.modifications_list}</div>
                </div>
              )}
            </div>
          )}

          {services.length > 0 && (
            <div className="section">
              <div className="section-title">Service History ({services.length} {services.length === 1 ? 'record' : 'records'})</div>
              <div className="table-scroll">
                <table className="record-table">
                  <thead>
                    <tr><th>Date</th><th>Garage</th><th>Mileage</th><th>Work carried out</th>{showPrivate && <th>Cost</th>}</tr>
                  </thead>
                  <tbody>
                    {services.map(s => (
                      <tr key={s.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{shortDate(s.service_date)}</td>
                        <td>{s.garage_name || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{s.mileage_at_service ? `${Number(s.mileage_at_service).toLocaleString()} km` : '—'}</td>
                        <td>{[s.work_carried_out, s.parts_replaced && `Parts: ${s.parts_replaced}`].filter(Boolean).join(' · ') || '—'}</td>
                        {showPrivate && <td>{money(s.cost)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {mechanical.length > 0 && (
            <div className="section">
              <div className="section-title">MOT / Inspection History ({mechanical.length} {mechanical.length === 1 ? 'record' : 'records'})</div>
              <div className="table-scroll">
                <table className="record-table">
                  <thead><tr><th>Date</th><th>Garage / Centre</th><th>Result</th><th>Next due</th>{showPrivate && <th>Cost</th>}</tr></thead>
                  <tbody>
                    {mechanical.map(m => (
                      <tr key={m.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{shortDate(m.report_date)}</td>
                        <td>{m.garage_name || '—'}</td>
                        <td style={{ color: m.result === 'Passed' ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{m.result || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{shortDate(m.next_due_date)}</td>
                        {showPrivate && <td>{money(m.cost)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {latestRt?.expiry_date && (
                <div className="note">Road tax: paid to {longDate(latestRt.expiry_date)}{isFuture(latestRt.expiry_date) ? '' : ' (expired)'}.</div>
              )}
            </div>
          )}

          {readings.length > 0 && (
            <div className="section keep">
              <div className="section-title">Recorded Odometer Readings ({readings.length})</div>
              <div className="table-scroll">
                <table className="record-table">
                  <thead><tr><th>Date</th><th>Reading</th><th>Source</th></tr></thead>
                  <tbody>
                    {latestReadings.map((r, i) => (
                      <tr key={i}>
                        <td>{shortDate(r.date)}</td>
                        <td>{r.km.toLocaleString()} km</td>
                        <td>{r.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="note">
                {readings.length > latestReadings.length ? `Showing the ${latestReadings.length} most recent of ${readings.length} readings. ` : ''}
                {mileageConsistent ? 'Recorded readings rise consistently over time.' : 'Some recorded readings are lower than earlier ones; ask the seller to explain.'}
              </div>
            </div>
          )}

          <div className="section keep">
            <div className="section-title">Paperwork on File</div>
            <div className="paper-grid">
              {paperwork.map(([label, n]) => (
                <div key={label} className={`paper-item${n ? '' : ' none'}`}>
                  <span>{label}</span><span>{n ? (n > 1 ? `${n} on file` : 'On file') : 'Not recorded'}</span>
                </div>
              ))}
            </div>
            <div className="note">Documents are held by the owner and can be shown on request.</div>
          </div>

          {showValue && valuation && (
            <div className="section keep">
              <div className="section-title">Valuation</div>
              <div className="money-grid">
                {[
                  ['Estimated value', money(valuation.current_value)],
                  ['Market low', money(valuation.lowest_sale)],
                  ['Market average', money(valuation.average_sale)],
                  ['Market high', money(valuation.highest_sale)],
                ].filter(([, v]) => v !== '—').map(([k, v]) => (
                  <div key={k}><div className="field-label">{k}</div><div className="field-value">{v}</div></div>
                ))}
              </div>
              <div className="note">Estimated value is based on comparable sales and the car's recorded details. It is a guide, not a valuation certificate.</div>
            </div>
          )}

          {showPrivate && (
            <>
              <div className="section keep">
                <div className="section-title">Owner Financials (private)</div>
                <div className="money-grid">
                  {[
                    ['Purchase price', purchasePrice !== null ? money(purchasePrice) : null],
                    ['Purchase date', valuation?.purchase_date ? longDate(valuation.purchase_date) : null],
                    ['Total invested', totalInvested !== null ? money(totalInvested) : null],
                    ['Estimated value', currentValue !== null ? money(currentValue) : null],
                    ['Gain / loss', gain !== null ? `${gain >= 0 ? '+' : '−'}${money(Math.abs(gain))} (${gainPct >= 0 ? '+' : ''}${gainPct.toFixed(1)}%)` : null],
                    ['Insurance agreed value', valuation?.insurance_agreed_value ? money(valuation.insurance_agreed_value) : null],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}>
                      <div className="field-label">{k}</div>
                      <div className="field-value" style={{ color: k === 'Gain / loss' ? (gain >= 0 ? '#16a34a' : '#dc2626') : undefined }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div className="note">Total invested = purchase price plus every cost recorded for this car (restoration, running costs, insurance, road tax, services and inspections).</div>
              </div>

              {insurance.length > 0 && (
                <div className="section">
                  <div className="section-title">Insurance Records (private)</div>
                  <div className="table-scroll">
                    <table className="record-table">
                      <thead><tr><th>Company</th><th>Type</th><th>Policy #</th><th>Start</th><th>Expiry</th><th>Cost</th></tr></thead>
                      <tbody>
                        {insurance.slice(0, 6).map(i => (
                          <tr key={i.id}>
                            <td>{i.insurance_company || '—'}</td>
                            <td>{i.insurance_type || '—'}</td>
                            <td style={{ fontFamily: 'Courier New, monospace', fontSize: 10 }}>{i.policy_number || '—'}</td>
                            <td>{shortDate(i.start_date)}</td>
                            <td>{shortDate(i.expiry_date)}</td>
                            <td>{money(i.cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {report && (
                <div className="section keep">
                  <div className="section-title">Investment Analysis (private)</div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div>
                      <div className="field-label" style={{ marginBottom: 6 }}>Verdict</div>
                      <div className="verdict-badge" style={{ background: `${verdictColor}18`, color: verdictColor, border: `1px solid ${verdictColor}44` }}>{report.investment_verdict}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div className="field-label" style={{ marginBottom: 4 }}>Summary</div>
                      <div style={{ fontSize: 12, color: '#374151', fontFamily: 'Arial, sans-serif', lineHeight: 1.6 }}>{report.verdict_reasoning}</div>
                    </div>
                  </div>
                  {(report.forecast_year_1 || report.forecast_year_5) && (
                    <div className="spec-grid">
                      {[['1 year', report.forecast_year_1], ['3 years', report.forecast_year_3], ['5 years', report.forecast_year_5], ['10 years', report.forecast_year_10]]
                        .filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => (
                          <div key={k} className="forecast">
                            <div className="field-label">{k}</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: v >= 0 ? '#16a34a' : '#dc2626', fontFamily: 'Arial, sans-serif' }}>{v >= 0 ? '+' : ''}{v}%</div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {galleryPhotos.length > 0 && (
            <div className="section">
              <div className="section-title">Photographs ({photos.length})</div>
              <div className="photo-grid">
                {galleryPhotos.map(p => (
                  <div key={p.id} className="photo-item">
                    <img src={photoUrl(p)} alt="Vehicle" style={cropStyle(p)} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="passport-footer">
            <div className="footer-brand">
              <strong>Car Collector Studio</strong> · Vehicle Passport<br />
              carcollectorstudio.com
            </div>
            <div className="footer-disclaimer">
              This passport was generated from information recorded by the owner. It has not been independently verified
              and is not an inspection, a warranty or a valuation certificate. Buyers should inspect the vehicle and check
              the original documents before purchase. Scores and values are estimates.
            </div>
          </div>

        </div>
      </div>
    </>
  );
};
